import type { Location } from '@/types/game';

/**
 * Locations follow the journey of the Fellowship as depicted in the
 * Peter Jackson film trilogy (Tom Bombadil & los Túmulos no aparecen en
 * las películas, así que se omiten).
 *
 * Companions unlock at `isRest: true` locations as a general rule. Bárbol
 * is the only exception (bound to Fangorn, the only place his character
 * could plausibly join).
 *
 * Combat zones can declare a `semiBoss` (mid-zone champion) and a `boss`
 * (zone-final encounter). See `src/engine/combat.ts` for the spawn rules.
 */
export const LOCATIONS: readonly Location[] = [
  {
    id: 'comarca',
    name: 'La Comarca',
    desc: 'La tierra pacífica de los Hobbits',
    enemies: [],
    killsNeeded: 0,
    isRest: true,
    companions: ['frodo', 'sam', 'merry', 'pippin'],
    // Only Frodo + Sam are required to begin the journey. Merry & Pippin
    // can be recruited later (player has to come back from Bosque Viejo).
    unlockGate: ['frodo', 'sam'],
    pos: [35.8, 26.1],
    background: '/backgrounds/hobbiton.jpg',
  },
  {
    id: 'bosque_viejo',
    name: 'Bosque Viejo',
    desc: 'El bosque encantado y peligroso',
    enemies: ['toad', 'cat'],
    killsNeeded: 35,
    semiBoss: 'forest_specter',
    boss: 'nazgul_rider',
    pos: [40.1, 26.9],
    background: '/backgrounds/bosque-viejo.jpg',
  },
  {
    id: 'bree',
    name: 'Bree',
    desc: 'La posada del Pony Pisador',
    enemies: [],
    killsNeeded: 0,
    isRest: true,
    companions: ['gandalf', 'aragorn'],
    pos: [42.4, 25.9],
    background: '/backgrounds/bree.png',
  },
  {
    id: 'cima_vientos',
    name: 'Cima de los Vientos',
    desc: 'Amon Sûl, la antigua atalaya',
    enemies: ['bandit', 'alpha_wolf'],
    killsNeeded: 80,
    semiBoss: 'khamul',
    boss: 'witch_king_amon',
    pos: [45.5, 25.5],
    background: '/backgrounds/amon-sul.png',
  },
  {
    id: 'rivendel',
    name: 'Rivendel',
    desc: 'El Último Hogar Acogedor',
    enemies: [],
    killsNeeded: 0,
    isRest: true,
    companions: ['arwen', 'legolas', 'gimli', 'boromir'],
    pos: [51.3, 25.1],
    background: '/backgrounds/rivendel.png',
    backgroundPosition: 'center center',
    backgroundSize: 'auto 130%',
  },
  {
    id: 'caradhras',
    name: 'Paso de Caradhras',
    desc: 'La Montaña Roja, nevada y traicionera',
    enemies: ['mountain_wolf', 'ice_golem'],
    killsNeeded: 90,
    semiBoss: 'crebain_flock',
    boss: 'caradhras_storm',
    pos: [51.8, 33.0],
    background: '/backgrounds/caradhras.jpg',
  },
  {
    id: 'moria_balin',
    name: 'Moria · Tumba de Balin',
    desc: 'Los orcos asaltan la Comunidad en la cámara de Mazarbul',
    enemies: ['moria_orc', 'moria_orc_archer'],
    killsNeeded: 150,
    semiBoss: 'great_orc',
    boss: 'cave_troll',
    pos: [51.2, 34.3],
    background: '/backgrounds/moria-balin.jpg',
  },
  {
    id: 'moria_puente',
    name: 'Moria · Puente de Khazad-dûm',
    desc: 'Huida hasta el puente; el Balrog despierta',
    enemies: ['moria_orc', 'moria_orc_archer'],
    killsNeeded: 80,
    semiBoss: 'cave_troll',
    boss: 'balrog',
    pos: [52.0, 35.5],
    background: '/backgrounds/moria-khazad-dum.png',
  },
  {
    id: 'lothlorien',
    name: 'Caras Galadhon',
    desc: 'La ciudad de los árboles en el corazón de Lothlórien',
    enemies: [],
    killsNeeded: 0,
    isRest: true,
    companions: ['galadriel', 'celeborn'],
    pos: [54.6, 37.8],
    background: '/backgrounds/caras-galadhon.jpg',
  },
  {
    id: 'anduin',
    name: 'Río Anduin',
    desc: 'El Gran Río y las Cascadas de Rauros',
    enemies: ['anduin_piranhas', 'water_serpent'],
    killsNeeded: 130,
    semiBoss: 'anduin_kraken',
    boss: 'hydra',
    pos: [59.1, 44.0],
    background: '/backgrounds/anduin.jpg',
  },
  {
    id: 'amon_hen',
    name: 'Amon Hen',
    desc: 'La Colina de la Vista, donde cae Boromir',
    enemies: ['uruk_hai_warrior', 'uruk_hai_berserker'],
    killsNeeded: 140,
    semiBoss: 'amon_hen_captain',
    boss: 'lurtz',
    pos: [58.3, 49.6],
    background: '/backgrounds/amon-hen.jpg',
  },
  {
    id: 'fangorn',
    name: 'Bosque de Fangorn',
    desc: 'El bosque milenario de los Ents, donde se ocultan los últimos Uruk-hai',
    enemies: ['fangorn_spider', 'fangorn_scorpion'],
    killsNeeded: 120,
    semiBoss: 'snaga',
    boss: 'grishnakh',
    companions: ['barbol'],
    hasShop: true,
    pos: [52.7, 45.6],
    background: '/backgrounds/fangorn.jpg',
  },
  {
    id: 'edoras',
    name: 'Edoras',
    desc: 'Capital del reino de Rohan',
    enemies: [],
    killsNeeded: 0,
    isRest: true,
    companions: ['gandalf_blanco', 'theoden'],
    pos: [51.7, 53.5],
    background: '/backgrounds/edoras.jpg',
  },
  {
    id: 'camino_a_abismo_helm',
    name: 'Camino al Abismo de Helm',
    desc: 'Emboscada de jinetes huargo en el camino',
    enemies: ['warg_rider', 'warg'],
    killsNeeded: 110,
    semiBoss: 'alpha_warg',
    boss: 'sharku',
    pos: [50.2, 53.0],
    background: '/backgrounds/camino-edoras.jpg',
  },
  {
    id: 'abismo_helm',
    name: 'Abismo de Helm',
    desc: 'La fortaleza de Rohan bajo asedio',
    enemies: ['helm_uruk_warrior', 'helm_uruk_berserker'],
    killsNeeded: 220,
    semiBoss: 'uruk_hai_captain',
    boss: 'ugthak',
    pos: [48.9, 51.8],
    background: '/backgrounds/abismo-helm.jpg',
  },
  {
    id: 'isengard',
    name: 'Isengard',
    desc: 'La torre de Saruman, Orthanc',
    enemies: ['uruk_hai_elite', 'isengard_orc'],
    killsNeeded: 160,
    semiBoss: 'wormtongue',
    boss: 'saruman',
    pos: [49.1, 46.3],
    background: '/backgrounds/isengard.jpg',
  },
  {
    id: 'campamento_rohan',
    name: 'Sagrario (Dunharrow)',
    desc: 'Punto de reunión de los Rohirrim antes de la guerra',
    enemies: [],
    killsNeeded: 0,
    isRest: true,
    companions: ['eowyn', 'eomer'],
    pos: [52.2, 55.7],
    background: '/backgrounds/dunharrow.jpg',
  },
  {
    id: 'paso_de_los_muertos',
    name: 'Senderos de los Muertos',
    desc: 'Bajo el Dwimorberg, donde habitan los juramentados',
    enemies: ['sworn_specter', 'dead_warrior'],
    killsNeeded: 180,
    semiBoss: 'dead_herald',
    boss: 'dead_king',
    companions: ['rey_muertos'],
    hasShop: true,
    pos: [50.9, 56.5],
    background: '/backgrounds/sendero-muertos.jpg',
  },
  {
    id: 'pelargir',
    name: 'Pelargir',
    desc: 'Puerto del Anduin tomado por los corsarios de Umbar',
    enemies: ['corsair', 'slaver'],
    killsNeeded: 180,
    semiBoss: 'master_corsair',
    boss: 'corsair_captain',
    pos: [58.7, 68.5],
    background: '/backgrounds/pelargir.png',
  },
  {
    id: 'ithilien',
    name: 'Ithilien',
    desc: 'Bosques al este del Anduin, dominio de Faramir',
    enemies: [],
    killsNeeded: 0,
    isRest: true,
    companions: ['faramir'],
    pos: [64.3, 56.3],
    background: '/backgrounds/ithilien.png',
  },
  {
    id: 'osgiliath',
    name: 'Osgiliath',
    desc: 'La antigua capital, hoy en ruinas',
    enemies: ['mordor_orc', 'mordor_orc_archer'],
    killsNeeded: 180,
    semiBoss: 'orc_captain',
    boss: 'winged_nazgul',
    pos: [62.9, 61.7],
    background: '/backgrounds/osgiliath.png',
  },
  {
    id: 'puertas_de_minas_tirith',
    name: 'Puertas de Minas Tirith',
    desc: 'Asalto a la Puerta de la Ciudad Blanca',
    enemies: ['mordor_orc', 'mordor_orc_archer', 'battle_troll'],
    killsNeeded: 220,
    semiBoss: 'armored_troll',
    boss: 'wolf_ram',
    pos: [61.9, 61.0],
    background: '/backgrounds/minas-tirith-door.jpg',
  },
  {
    id: 'pelennor',
    name: 'Campos del Pelennor',
    desc: 'La gran batalla por Gondor',
    enemies: ['haradrim', 'haradrim_scout', 'mordor_orc'],
    killsNeeded: 260,
    semiBoss: 'mumakil',
    boss: 'witch_king',
    pos: [62.4, 60.5],
    background: '/backgrounds/pelennor.jpg',
  },
  {
    id: 'minas_tirith',
    name: 'Minas Tirith',
    desc: 'La Ciudad Blanca de Gondor',
    enemies: [],
    killsNeeded: 0,
    isRest: true,
    companions: ['rey_aragorn'],
    pos: [61.4, 60.7],
    background: '/backgrounds/minas-tirith.png',
  },
  {
    id: 'minas_morgul',
    name: 'Minas Morgul',
    desc: 'La Ciudad de los Espectros',
    enemies: ['morgul_orc', 'morgul_orc_archer'],
    killsNeeded: 210,
    semiBoss: 'nazgul_dragon',
    boss: 'gothmog',
    pos: [65.5, 61.4],
    background: '/backgrounds/minas-morgul.png',
  },
  {
    id: 'guarida_shelob',
    name: 'Guarida de Shelob',
    desc: 'Túneles oscuros y telarañas en el corazón del paso',
    enemies: ['small_spider', 'web'],
    killsNeeded: 220,
    semiBoss: 'great_spider',
    boss: 'shelob',
    pos: [65.9, 59.6],
    background: '/backgrounds/cirith-ungol.png',
  },
  {
    id: 'cirith_ungol',
    name: 'Cirith Ungol',
    desc: 'El paso secreto a Mordor',
    enemies: ['mordor_orc', 'mordor_orc_archer'],
    killsNeeded: 200,
    semiBoss: 'gorbag',
    boss: 'shagrat',
    pos: [66.5, 59.9],
    background: '/backgrounds/cirith-ungol.png',
  },
  {
    id: 'puerta_negra',
    name: 'Puerta Negra',
    desc: 'Morannon, entrada a Mordor',
    enemies: ['mordor_orc', 'mordor_orc_archer', 'mordor_troll'],
    killsNeeded: 240,
    semiBoss: 'armored_cave_orc',
    boss: 'mouth_of_sauron',
    pos: [66.8, 52.0],
    background: '/backgrounds/black-gate.png',
  },
  {
    id: 'gorgoroth',
    name: 'Planicie de Gorgoroth',
    desc: 'Las tierras yermas y oscuras de Mordor',
    enemies: ['elite_orc', 'mordor_elite_troll'],
    killsNeeded: 300,
    semiBoss: 'eye_of_sauron',
    boss: 'sauron_spirit',
    pos: [68.2, 58.3],
    background: '/backgrounds/gorgoroth.png',
  },
  {
    id: 'monte_destino',
    name: 'Monte del Destino',
    desc: 'Orodruin, donde fue forjado el Anillo. Solo Gollum y el Anillo aguardan.',
    enemies: [],
    killsNeeded: 0,
    semiBossAt: 0,
    bossAt: 0,
    semiBoss: 'gollum',
    boss: 'one_ring',
    isFinal: true,
    pos: [70.6, 57.8],
    background: '/backgrounds/mount-doom.jpg',
  },
];
